// OAuthCallback.js - Add this as a new component in your React app
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { SiteStyles } from '../site/SiteChrome';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCurrentUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Get parameters from URL
      const token = searchParams.get('token');
      const refresh = searchParams.get('refresh');
      const provider = searchParams.get('provider');
      const authStatus = searchParams.get('status');
      const error = searchParams.get('error');
      const errorMessage = searchParams.get('message');

      // SECURITY FIX #4: Immediately clear tokens from the URL so they don't
      // appear in browser history, server access logs, or Referer headers.
      // Do this before ANY async work or outbound requests.
      window.history.replaceState({}, document.title, window.location.pathname);

      if (process.env.NODE_ENV === 'development') {
        console.log('OAuth Callback:', {
          hasToken: !!token,
          hasRefresh: !!refresh,
          provider,
          authStatus,
          hasError: !!error,
        });
      }

      // Handle errors
      // SECURITY FIX #7 (applied here too): Never render raw URL params.
      const OAUTH_ERROR_MESSAGES = {
        google_auth_failed:    'Google authentication failed. Please try again.',
        state_mismatch:        'Security validation failed. Please try again.',
        no_code:               'Authentication was cancelled. Please try again.',
        token_exchange_failed: 'Failed to complete authentication. Please try again.',
        oauth_callback_failed: 'Authentication failed. Please try again or register manually.',
      };

      if (error) {
        setStatus('error');
        setMessage(
          OAUTH_ERROR_MESSAGES[error] ||
          'Authentication failed. Please try again or register manually.'
        );
        setTimeout(() => {
          navigate('/register', {
            state: {
              error: `${provider || 'OAuth'} authentication failed. Please try again or register manually.`,
            },
          });
        }, 3000);
        return;
      }

      // Handle successful authentication
      if (token && refresh) {
        try {
          // Store tokens
          localStorage.setItem('access_token', token);
          localStorage.setItem('refresh_token', refresh);

          // SECURITY FIX #9: Decode the JWT payload only for display/UX purposes
          // (e.g. showing the user's name). NEVER use client-side decoded claims
          // to set roles, permissions, or approval_status — those must come from
          // a server-verified API response (/api/auth/me).
          let tokenData = {};
          try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
              atob(base64).split('').map(c =>
                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
              ).join('')
            );
            tokenData = JSON.parse(jsonPayload);
          } catch {
            // Ignore decode errors — we'll fetch the real user data from the API
          }

          // Fetch the authoritative user record from the server instead of
          // trusting the JWT payload for sensitive fields.
          try {
            const meResponse = await fetch('/api/auth/me', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (meResponse.ok) {
              const meData = await meResponse.json();
              if (setCurrentUser && meData) {
                setCurrentUser({
                  id: meData.id,
                  username: meData.username,
                  email: meData.email,
                  first_name: meData.first_name,
                  last_name: meData.last_name,
                  roles: meData.roles || [],
                  permissions: meData.permissions || [],
                  approval_status: meData.approval_status || authStatus || 'pending',
                });
              }
            }
          } catch {
            // Fallback to minimal JWT display data (no roles/permissions)
            if (setCurrentUser && tokenData.sub) {
              setCurrentUser({
                id: tokenData.sub,
                username: tokenData.username,
                email: tokenData.email,
                first_name: tokenData.first_name,
                last_name: tokenData.last_name,
                roles: [],
                permissions: [],
                approval_status: authStatus || 'pending',
              });
            }
          }

          setStatus('success');
          setMessage(`Successfully authenticated with ${provider || 'OAuth'}!`);

          // Redirect based on account status AND subscription
          setTimeout(async () => {
            if (authStatus === 'approved') {
              try {
                const subResponse = await fetch('/api/subscription/status', {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const subData = await subResponse.json();

                if (subData.is_active) {
                  navigate('/live-watch');
                } else {
                  localStorage.removeItem('access_token');
                  localStorage.removeItem('refresh_token');
                  localStorage.removeItem('currentUser');
                  localStorage.removeItem('subscriptionInfo');
                  navigate('/subscription?expired=true&from=oauth');
                }
              } catch (subErr) {
                console.error('Subscription check failed during OAuth:', subErr);
                // RECOVERY FIX: A network failure is NOT proof of "no subscription".
                // Previously this logged out valid subscribers. Keep the tokens and
                // let ProtectedRoute's retry logic make the real determination.
                navigate('/live-watch');
              }
            } else if (authStatus === 'rejected') {
              navigate('/registration-rejected');
            } else {
              navigate('/pending-approval');
            }
          }, 2000);

        } catch (err) {
          console.error('Error processing OAuth tokens:', err);
          setStatus('error');
          setMessage('Failed to process authentication. Please try again.');
          setTimeout(() => { navigate('/register'); }, 3000);
        }
      } else {
        setStatus('error');
        setMessage('No authentication data received. Please try again.');
        setTimeout(() => { navigate('/register'); }, 3000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, setCurrentUser]);

  return (
    <div className="dark ts-page">
    <SiteStyles />
    <div className="min-h-screen bg-[#07131F] flex items-center justify-center p-4">
      <div className="bg-[#0D1E2E] border border-[#1C3348] rounded-md p-8 max-w-md w-full">
        <div className="text-center">
          {/* Status Icon */}
          <div className="mb-6">
            {status === 'processing' && (
              <div className="mx-auto w-8 h-8 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#5AA9F0] animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="mx-auto w-8 h-8 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
            )}
            {status === 'error' && (
              <div className="mx-auto w-8 h-8 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-[#F0414F]" />
              </div>
            )}
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-white mb-2">
            {status === 'processing' && 'Authenticating...'}
            {status === 'success' && 'Authentication Successful!'}
            {status === 'error' && 'Authentication Failed'}
          </h2>

          {/* Message */}
          <p className="text-slate-400 mb-6">{message}</p>

          {/* Additional Info for Success */}
          {status === 'success' && (
            <div className="bg-[#07131F] border border-[#28445F] rounded-md p-4 mb-4">
              <div className="flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-[#5AA9F0] mr-2" />
                <span className="text-sm text-slate-300">
                  Redirecting you to the appropriate page...
                </span>
              </div>
            </div>
          )}

          {/* Manual Navigation Button (fallback) */}
          {status === 'error' && (
            <button
              onClick={() => navigate('/register')}
              className="mt-4 px-6 py-2 bg-[#5AA9F0] text-[#07131F] font-semibold rounded-md hover:bg-[#8CC4F5] transition-colors"
            >
              Back to Registration
            </button>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default OAuthCallback;